import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { useCanUsePaymentRequestButtonMethods } from 'src/app/shared/components/payments';
import { useLoginState } from 'src/app/shared/hooks/session';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';
import type { PaymentType } from 'src/app/shared/utils/payments';

import { usePromotePageData } from '../pageData';

import { AvailablePaymentMethodsContext } from './context';
import { useTreatSavedPaymentExperiment } from './hooks/useTreatSavedPaymentExperiment';
import { resolveSavedPaymentMethodContent } from './utils/resolveSavedPaymentMethodContent';

export const AvailablePaymentMethodsProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	const {
		data: {
			featureConfigs: { paymentMethodsEnabledFor, paypalMobileDefault },
			user: { savedPaymentMethods: userSavedPaymentMethods },
		},
	} = usePromotePageData();
	const isMobile = useIsMobile();
	const canUsePaymentRequestButtonMethods = useCanUsePaymentRequestButtonMethods();
	const loginState = useLoginState();

	const { paymentMethods, savedPaymentMethods } = useMemo(() => {
		const availablePaymentMethods: PaymentType[] = [];
		if (!paymentMethodsEnabledFor) {
			availablePaymentMethods.push('creditCard');
		} else {
			paymentMethodsEnabledFor.forEach((paymentType) => {
				switch (paymentType.payment_method_name) {
					case 'paypal':
						availablePaymentMethods[isMobile && paypalMobileDefault ? 'unshift' : 'push']('paypal');
						break;
					case 'applePay':
					case 'googlePay':
						if (canUsePaymentRequestButtonMethods[paymentType.payment_method_name])
							availablePaymentMethods.push(paymentType.payment_method_name);
						break;
					case 'sepa':
						// "email" is required for sepa, but we are not able to retrieve that value for identified users.
						// this payment method will be hidden for identified users.
						if (loginState !== 'IDENTIFIED') availablePaymentMethods.push('sepa');
						break;
					default:
						availablePaymentMethods.push(paymentType.payment_method_name);
				}
			});
		}
		return {
			paymentMethods: availablePaymentMethods,
			savedPaymentMethods: resolveSavedPaymentMethodContent(userSavedPaymentMethods, availablePaymentMethods),
		};
	}, [
		canUsePaymentRequestButtonMethods,
		isMobile,
		paymentMethodsEnabledFor,
		paypalMobileDefault,
		loginState,
		userSavedPaymentMethods,
	]);

	useTreatSavedPaymentExperiment(savedPaymentMethods);

	return (
		<AvailablePaymentMethodsContext.Provider
			value={{
				paymentMethods,
				savedPaymentMethods,
			}}
		>
			{children}
		</AvailablePaymentMethodsContext.Provider>
	);
};
